import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// API
import { getSupportRequestsTree } from "@/Washouts/api/SupportRequests/queries";

// Custom components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import SupportRequestsContainer from "./Container";
// Helpers
import { findPartnerPayPeriod } from "@/Common/helpers/PayPeriods";
import { parseIdsFromSelectedTreeNode } from "@/Washouts/helpers/ChangeRequest";
import { getPrioritizedStatus } from "@/Washouts/helpers/Statuses";

// Types
import { RootState } from "@/redux/store";
import { ExtendedTreeNode, ISupportRequestsSidebarProps, SupportRequestsSidebar } from "./Sidebar";
// Hooks
import { useChangeRequestSubscriptions } from "@/Washouts/hooks/ChangeRequests/useChangeRequestSubscriptions";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";

const getPayPeriod = (commissionCycles: ICommissionCycle[], args: { commissionCycleId?: string; payPeriodId?: string }) => {
	const { commissionCycleId = "", payPeriodId = "" } = args ?? {};
	if (!commissionCycles?.length || !commissionCycleId || !payPeriodId) {
		return null;
	}
	const commissionCycle = commissionCycles.find((cycle) => cycle._id === commissionCycleId);
	return commissionCycle?.payPeriods.find((period) => period._id === payPeriodId);
};

const getCloseActiveCycle = (
	commissionCycles: ICommissionCycle[],
	commissionCycleId?: string
): ICommissionCycle | { payPeriods: IPayPeriod[] } =>
	commissionCycles.filter((cycle) => cycle.status === "ACTIVE").find((cycle) => cycle._id === commissionCycleId) ?? { payPeriods: [] };

const getPositionStatus = (payPeriod: IPayPeriod, position: string) =>
	payPeriod?.positionStatus?.find((item) => item.position === position) ?? { position, status: "UNKNOWN" };

const isSectionedPeriodInReview = (commissionCycles: ICommissionCycle[], payPeriod: IPayPeriod, position = "") => {
	const partnerPeriod = findPartnerPayPeriod({ commissionCycles, payPeriod });
	if (!payPeriod || !partnerPeriod) {
		return false;
	}
	const payPeriodStatus = getPositionStatus(payPeriod, position);
	const partnerPeriodStatus = getPositionStatus(partnerPeriod, position);
	const status = getPrioritizedStatus(payPeriodStatus, partnerPeriodStatus);
	return status === "PENDING";
};

const checkCanCreateChangeRequests = (args: {
	commissionCycle?: string;
	payPeriod?: string;
	position?: string;
	commissionCycles?: ICommissionCycle[];
}) => {
	const { payPeriod, position, commissionCycle, commissionCycles } = args ?? {};

	if (!commissionCycle || !position || !commissionCycles?.length) {
		return false;
	}

	const selectedPeriod = getPayPeriod(commissionCycles, { commissionCycleId: commissionCycle, payPeriodId: payPeriod });
	if (selectedPeriod?.previousCCId) {
		return isSectionedPeriodInReview(commissionCycles, selectedPeriod, position);
	}

	const { payPeriods } = getCloseActiveCycle(commissionCycles, commissionCycle);
	if (!payPeriods?.length) {
		return false;
	}
	return payPeriods.some((period) => period.positionStatus.some((item) => item.position === position && item.status === "PENDING"));
};
export const Wrapper = () => {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const currentUserId = useSelector((state: RootState) => state.user.user?._id ?? "");

	// States
	const [open, setOpen] = useState(true);
	const [selected, setSelected] = useState<ExtendedTreeNode | null>(null);
	const [currentTree, setCurrentTree] = useState<ISupportRequestsSidebarProps["currentTree"]>("ReviewTree");

	const { data: fullTree, isLoading } = useQuery({
		queryKey: ["supportRequestsTree", locationId],
		queryFn: async () => {
			if (!locationId) {
				throw new Error("No locationId");
			}
			const result = await getSupportRequestsTree({ locationId });
			if (!result) {
				throw new Error("No data found");
			}
			return result;
		},

		enabled: !!locationId,
		staleTime: Infinity,
	});

	const handleSelection: ISupportRequestsSidebarProps["handleSelection"] = function (node, tree) {
		setSelected(node);
		setCurrentTree(tree);
		setOpen(!open);
	};

	const { commissionCycle, payPeriod, position } = parseIdsFromSelectedTreeNode(selected, "");
	const { commissionCycles } = useCommissionCyclesData();

	const canCreateChangeRequests = useMemo(
		() => checkCanCreateChangeRequests({ commissionCycle, payPeriod, position, commissionCycles }),
		[commissionCycle, payPeriod, position, commissionCycles]
	);

	useChangeRequestSubscriptions({
		position,
		commissionCycleId: commissionCycle,
		userId: currentUserId,
		enabled: !!commissionCycle,
	});

	const { clearSubscriptionContext } = useSubscriptions();

	useEffect(() => {
		if (!location) {
			return () => {};
		}

		return () => {
			setOpen(true);
			setSelected(null);
			clearSubscriptionContext();
		};
	}, [location]);

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					open={true}
					permanentTrigger={open}
					control={
						<SupportRequestsSidebar
							currentTree={currentTree}
							handleSelection={handleSelection}
							isLoading={isLoading}
							selected={selected}
							tree={fullTree}
						/>
					}
				/>
			}
		>
			<SupportRequestsContainer currentTree={currentTree} canCreateChangeRequests={canCreateChangeRequests} selected={selected} />
		</BodyLayer>
	);
};
